import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { TextInput } from "../../components/textInput";
import { Toast } from "../../components/toast";
import { SideSheet } from "../../components/sheet/sideSheet";
import { ImageSelectMenu } from "../../components/select/image";
import { SearchSiteInput } from "../../components/textInput/searchSiteInput";
import { GeofenceEditor } from "../../components/maps/GeofenceEditor";
import { EmergencyContacts } from "./emergencyContacts";
import {
  useForm,
  FormProvider,
  Controller,
  useFieldArray,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

/// Form validation schema ///
const schema = z.object({
  site_name: z.string().min(1, "Site name is required"),
  site_address: z
    .object({
      street: z.string().min(1, "Street is required"),
      city: z.string().min(1, "City is required"),
      state: z.string().min(1, "State is required"),
      country: z.string().min(1, "Country is required"),
      zip: z.string().optional(),
    })
    .refine(
      (data) => {
        // Check if all fields are empty (considering that 'zip' is optional)
        return data.street || data.city || data.state || data.country;
      },
      {
        message: "At least one address field is required",
        path: ["site_address"],
      }
    ),
  site_notes: z.string().min(1, "Site notes are required"),
  // The clock-in area is optional data — an incomplete fence (e.g. an
  // address-less site with no center) is fine and simply never enforced.
  geofence: z
    .object({
      latitude: z.number().nullable().optional(),
      longitude: z.number().nullable().optional(),
      radius_m: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  customer_id: z
    .number()
    .min(1, "Customer is required")
    .or(z.literal(null).refine(() => false, "Customer is required")),
  emergency_contacts: z
    .array(
      z
        .object({
          id: z.number().optional(),
          type_id: z.number().optional(),
          number: z
            .string()
            .optional()
            .refine(
              (val) => {
                // Check if val is not provided or if it matches the regex
                // This regex matches a string that starts with + followed by 10 to 15 digits
                return !val || /^\+\d{10,15}$/.test(val);
              },
              {
                message:
                  "Invalid phone number format. Enter a number with + and 10 to 15 digits.",
              }
            ),
        })
        .refine(
          (data) => {
            // Ensure both type_id and number are provided if one is given
            return !!data.type_id === !!data.number;
          },
          {
            message:
              "Both type_id and number should be provided if one is given",
            path: ["emergency_contacts"],
          }
        )
    )
    .optional(),
});

export default schema;

// Every site carries a clock-in area; default the radius so it shows a
// sensible circle even before the admin touches it (center comes from the
// geocoded address).
const DEFAULT_AREA_RADIUS_M = 150;
const withAreaDefaults = (g) => ({
  latitude: g?.latitude ?? null,
  longitude: g?.longitude ?? null,
  radius_m: g?.radius_m ?? DEFAULT_AREA_RADIUS_M,
});

function SiteDetailsMeta({
  isOpen,
  toggleOpen,
  updateSiteData,
  isAddMode,
  onSuccess,
  siteData,
}) {
  const params = useParams();
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();

  /// Contacts ///
  const [contacts, setContacts] = useState([
    { id: "", site_id: "", type_id: "", number: "" },
  ]);

  // function formatAddress({ street, city, state, zip, country }) {
  //   // Join the address components into a single string.
  //   return `${street}, ${city}, ${state}, ${zip}, ${country}`;
  // }

  const [submitSuccess, setSubmitSuccess] = useState({
    displaying: false,
    message: "Site updated successfully",
  });

  /// Form ///
  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      site_name: siteData?.site_name || "",
      site_address: siteData?.site_address || {
        street: "",
        city: "",
        state: "",
        country: "",
        zip: "",
      },
      site_notes: siteData?.site_notes || "",
      geofence: withAreaDefaults(siteData?.geofence),
      customer_id: siteData?.customer_id || null,
      emergency_contacts: siteData?.emergency_contacts || [
        { id: "", type_id: "", number: "" },
      ],
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "emergency_contacts",
  });

  useEffect(() => {
    if (isOpen && isAddMode) {
      reset({
        site_name: "",
        site_address: {
          street: "",
          city: "",
          state: "",
          country: "",
          zip: "",
        },
        site_notes: "",
        geofence: withAreaDefaults(null),
        customer_id: null,
      });
    }
  }, [isOpen, isAddMode, reset]);

  /// Toast ///
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const handleResponse = (message, type) => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const onSubmit = (data) => {
    return isAddMode ? createSite(data) : updateSite(data);
  };

  const handleSave = handleSubmit(onSubmit);

  useEffect(() => {
    if (siteData) {
      setValue("site_name", siteData.site_name);
      if (
        siteData.street &&
        typeof siteData.site_address === "object"
      ) {
        setValue(
          "site_address.street",
          siteData.site_address.street
        );
        setValue("site_address.city", siteData.site_address.city);
        setValue("site_address.state", siteData.site_address.state);
        setValue(
          "site_address.country",
          siteData.site_address.country
        );
        setValue("site_address.zip", siteData.site_address.zip);
      } else {
        // Handle case where site_address is not an object, or is missing
        setValue("site_address", {});
      }
      setValue("site_notes", siteData.site_notes);
      setValue("geofence", withAreaDefaults(siteData.geofence));
      setValue("customer_id", siteData.customer_id);
    }
  }, [siteData, setValue]);

  const onError = (errors, event) => {
  };

  /// Side sheet ///
  const [open, setOpen] = useState(isOpen);
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  /// Customers ///
  const [customers, setCustomers] = useState([{}]);
  useEffect(() => {
    authFetch(
      `${process.env.REACT_APP_API_URL}/v1/customers`
    )
      .then((respose) => respose.json())
      .then((data) => {
        setCustomers(data);
      });
  }, [orgId]);

  /// Address ///
  const [siteAddress, setSiteAddress] = useState(
    siteData?.site_address || {
      street: "",
      city: "",
      state: "",
      country: "",
      zip: "",
    }
  );

  useEffect(() => {
    if (siteData?.site_address) {
      setSiteAddress(siteData.site_address);
    }
  }, [siteData]);

  // Coordinates of the last address chosen via Places autocomplete, used to
  // seed the geofence center without a separate geocoding call.
  const [geofenceSeedCoords, setGeofenceSeedCoords] = useState(null);

  const [resetSiteSelected, setResetSiteSelected] = useState(0);

  function setSiteReset() {
    setResetSiteSelected((c) => c + 1); // Increment the counter to trigger change
  }

  /// Fetch emergency contact types ///
  const [contactTypes, setContactTypes] = useState([]);

  useEffect(() => {
    async function fetchContactTypes() {
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_API_URL}/v1/emergency-contacts/types`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setContactTypes(data); // Assuming this state is declared in the parent component
      } catch (error) {
        console.error("Error fetching contact types:", error);
      }
    }
    fetchContactTypes();
  }, []);

  /// Set site id ///
  const site_id = params.id;

  // Fetch and set emergency contacts
  useEffect(() => {
    const fetchContacts = async () => {
      if (!site_id) return;

      try {
        const response = await authFetch(
          `${process.env.REACT_APP_API_URL}/v1/emergency-contacts?site_id=${site_id}`
        );
        if (!response.ok)
          throw new Error(`Failed to fetch contacts: ${response.statusText}`);

        const fetchedData = await response.json();
        const formattedContacts = fetchedData.map((contact) => ({
          id: contact.id,
          type_id: contact.type_id,
          number: contact.number.startsWith("+1")
            ? contact.number
            : `+1${contact.number}`,
        }));

        // Set fetched and formatted contacts as the default value
        setValue("emergency_contacts", formattedContacts, {
          shouldValidate: true,
        });

      } catch (error) {
        console.error("Error fetching emergency contacts:", error);
      }
    };

    fetchContacts();
  }, [site_id, setValue]);

  /// Create site ///
  function createSite(data) {
    const isContactEmpty = (contact) => {
      return (
        !contact.id ||
        !contact.site_id ||
        !contact.type_id ||
        !contact.number
      );
    };

    const hasEmptyContact = data.emergency_contacts.some((contact) => {
      return !contact.type_id && !contact.number;
    });

    if (hasEmptyContact) {
      setContacts([]);
    }

    const payload = {
      ...data,
      contacts: data.emergency_contacts.map((contact) => ({
        id: contact.id,
        type_id: contact.type_id,
        number: contact.number,
      })),
      customer_id: data.customer_id,
    };


    authFetch(`${process.env.REACT_APP_API_URL}/v1/sites`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(`Fetch returned status ${response.status}`);
        }
        return response.json();
      })
      .then((result) => {
        onSuccess();
        handleResponse("Site created successfully", "success");
        reset();
        setSiteReset();
      })
      .catch((error) => {
        console.error("Error:", error);
        handleResponse("Failed to create site", "danger");
      });
  }

  /// Update site ///
  function updateSite(data) {
    const emergencyContactsPayload = data.emergency_contacts.map((contact) => ({
      id: contact.id,
      type_id: contact.type_id,
      number: contact.number,
    }));

    const payload = {
      ...data,
      org_id: orgId,
      contacts: emergencyContactsPayload,
      customer_id: data.customer_id,
    };

    authFetch(`${process.env.REACT_APP_API_URL}/v1/sites/${params.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Fetch returned status ${response.status}`);
        return response.json();
      })
      .then(() => {
        onSuccess();
        handleResponse("Site updated successfully", "success");
        // reset();
      })
      .catch((error) => {
        console.error("Error:", error);
        handleResponse("Failed to update site", "danger");
      });
  }

  return (
    <div>
      <FormProvider {...methods}>
        <SideSheet
          isOpen={open}
          onClose={toggleOpen}
          title={!isAddMode ? "Site details" : "Create site"}
          isAddMode={isAddMode}
          footerText={isAddMode ? "Create site" : "Update site"}
          onError={onError}
          handleSave={handleSave}
        >
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 min-h-[calc(100vh-200px)]">
            <div className="space-y-6 pb-20">
              <TextInput name="site_name" label="Site name" />
              <SearchSiteInput
                name="site_address"
                label="Site address"
                defaultValue={siteAddress}
                onAddressChange={(newAddress) => setSiteAddress(newAddress)}
                onCoordinatesChange={(coords) => setGeofenceSeedCoords(coords)}
                defaultAddress={siteAddress}
                onReset={resetSiteSelected}
                isAddMode={isAddMode}
              />
              <TextInput name="site_notes" label="Site notes" />
              <Controller
                name="geofence"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <GeofenceEditor
                      value={value}
                      onChange={onChange}
                      seedCoords={geofenceSeedCoords}
                      address={[
                        siteAddress?.street,
                        siteAddress?.city,
                        siteAddress?.state,
                        siteAddress?.zip,
                        siteAddress?.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                    {errors.geofence && (
                      <p className="mt-1 text-sm text-danger">
                        {errors.geofence.message}
                      </p>
                    )}
                  </>
                )}
              />
              <Controller
                name="customer_id"
                control={control}
                render={({ field: { onChange, value, name, ref } }) => (
                  <ImageSelectMenu
                    label="Customer"
                    options={customers}
                    value={value}
                    onChange={(selectedId) => {
                      onChange(selectedId);
                    }}
                    error={errors.customer_id}
                  />
                )}
              />
              <EmergencyContacts
                siteId={params.id}
                fields={fields}
                onAddContact={() => append({ type_id: "", number: "" })}
                onRemoveContact={(index) => remove(index)}
                error={errors.emergency_contacts}
                contactTypes={contactTypes}
              />
            </div>
          </div>
        </SideSheet>
      </FormProvider>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={hideToast}
      />
    </div>
  );
}
export { SiteDetailsMeta };
